import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppService } from '@app/app.service';
import { CompanyModel } from '@data-layer/models/companies';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { DocumentModel } from '@data-layer/models/documents';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'es-batch-review-sidebar',
  templateUrl: './es-batch-review-sidebar.component.html',
  styleUrls: ['./es-batch-review-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsBatchReviewSidebarComponent implements OnInit, OnDestroy {
  @Input() public referenceNumber: string;
  @Input() public isChecked: boolean;
  @Input() public isBatch: boolean;
  @Output() public review: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public validChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public reloadList: EventEmitter<void> = new EventEmitter<void>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();

  public isConfirmedFc: UntypedFormControl;
  public isLoading: boolean;

  public entrySummary: EntrySummaryModel;
  public company: CompanyModel;

  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;

  private esValidations: { [name: string]: boolean };
  private documents: DocumentModel[] = [];
  private updateCount: number = 0;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private appService: AppService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  public ngOnInit(): void {
    this.isConfirmedFc = new UntypedFormControl(this.isChecked);
    this.isConfirmedFc.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isConfirmed: boolean) => this.review.emit(isConfirmed));

    this.company = this.companiesService.getCompany();

    this.isLoading = true;
    this.entrySummaryStateService.entrySummary$
      .pipe(
        filter((entrySummary: EntrySummaryModel) => !!entrySummary),
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$),
      )
      .subscribe((entrySummary: EntrySummaryModel) => {
        this.entrySummary = entrySummary;

        this.reloadList.emit();

        this.cdr.markForCheck();
      });

    this.entrySummaryStateService.entryDocuments$.pipe(takeUntil(this.destroy$)).subscribe((documents: DocumentModel[]) => {
      this.documents = documents;

      this.cdr.markForCheck();
    });

    this.entrySummaryStateService.validationsList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((esValidations: { [name: string]: boolean }) => {
        this.esValidations = esValidations;
      });

    combineLatest([
      this.entrySummaryStateService.entrySummary$,
      this.entrySummaryStateService.entryDocuments$,
      this.entrySummaryStateService.validationsList$,
    ])
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!!this.entrySummary && !!this.documents && !!this.esValidations) {
          this.updateCount++;
          this.updateESValid(this.entrySummary);

          this.cdr.markForCheck();
        }
      });

    this.entrySummaryStateService
      .loadEntrySummary(this.referenceNumber)
      .pipe(
        tap((es: EntrySummaryModel) => this.entrySummaryStateService.entrySummary$.next(es)),
        switchMap(() => this.entrySummaryStateService.updateDocuments(true)),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cdr.markForCheck());
  }

  public ngOnDestroy(): void {
    this.entrySummaryStateService.entrySummary$.next(null);
    this.entrySummaryStateService.entryDocuments$.next(null);
    this.destroy$.next();
  }

  public closeSidebar(): void {
    // means that at least something was updated -> reload parent list
    if (this.updateCount > 1) {
      this.reloadList.emit();
    }

    this.close.emit();
  }

  private updateESValid(entrySummary: EntrySummaryModel): void {
    this.entrySummary = { ...entrySummary, valid: this.esValidations[this.entrySummary?.referenceNumber] && this.isFilesValid };

    if (!this.entrySummary.valid) {
      this.isConfirmedFc.setValue(false, { emitEvent: false });
      this.validChange.emit(false);
      this.isConfirmedFc.disable();
    } else {
      this.validChange.emit(true);
      this.isConfirmedFc.enable();
    }
  }

  private get isFilesValid(): boolean {
    return this.entrySummaryStateService.isFilesValid(this.entrySummary, this.documents);
  }
}
